import React, { SyntheticEvent, useEffect, useState } from 'react';

import '../../assets/stylesheets/molecules/image-crop.scss';
import { useHasViewPermission } from '../../hooks/useHasPermission';
import { useToggle } from '../../hooks/useToggle';
import { useCanViewLargeLicensePlate } from '../../hooks/useAssetManagement';
import { useHasAccess } from '../../hooks/useHasAccess';
import { Event } from '../../interfaces';
import { getEventLicensePlateURL, getEventRawLicensePlateURL } from '../../utils';
import { ZoomableImage } from './imagesWithOverlays/zoomableImage';
import { Modal } from './modal';
import { Padded } from './padded';
import { Centered } from './centered';

interface Props {
  event: Event;
  targetWidth: number;
  showRaw?: boolean | 'hover';
}

const border_horizontal = 24; // additional left and right padding around the plate in pixels
const border_vertical = 24; // additional top and bottom padding around the plate in pixels

export function LicencePlate(props: Props) {
  const { event, targetWidth } = props;
  const [modalVisible, toggleModal] = useToggle(false);
  const hasAccess = useHasAccess();
  const canView = useHasViewPermission('events:read:resource');

  const rawUrl = getEventRawLicensePlateURL(event);
  const enhancedUrl = getEventLicensePlateURL(event);
  const [imageURL, setImageUrl] = useState<string>(props.showRaw === true ? rawUrl : enhancedUrl);

  const canViewLargeLP = useCanViewLargeLicensePlate();

  useEffect(() => {
    setImageUrl(props.showRaw === true ? rawUrl : enhancedUrl);
  }, [event, props.showRaw]);

  if (!event || !canView) {
    return null;
  }

  let { plate_crop, event_type_identifier } = event;
  const targetHeight = targetWidth / 1.5; // sets max height for unusual angles

  // TODO: fix to show something in enhanced and original image for TEST events
  if (plate_crop === '0;0;0;0;0;0;0;0' && event_type_identifier === 'TEST') {
    plate_crop = '100;100;200;100;200;200;100;200';
  }

  if (!plate_crop || plate_crop === '') {
    return null;
  }

  const parseCoordinates = (coordinates: string) => {
    const coords = coordinates.split(';').map((v) => parseInt(v, 10));
    return {
      xCoords: [coords[0], coords[2], coords[4], coords[6]],
      yCoords: [coords[1], coords[3], coords[5], coords[7]],
    };
  };

  const getMaxMinCoords = (coordinates: string) => {
    const { xCoords, yCoords } = parseCoordinates(coordinates);
    return {
      xMax: Math.min(...xCoords),
      xMin: Math.max(...xCoords),
      yMax: Math.max(...yCoords),
      yMin: Math.min(...yCoords),
    };
  };

  const getPlacementOptions = (coordinates: string, targetWidth: number) => {
    const { xMax, xMin, yMax, yMin } = getMaxMinCoords(coordinates);

    //
    // Dimensions of LPR
    const lprWidthWithBorder = Math.abs(xMax - xMin) + border_horizontal * 2;
    const lprHeightWithBorder = Math.abs(yMax - yMin) + border_vertical * 2;
    // Zoomlevel - scale to fit the target area (allow zoom > 1 to fill space when expanding)
    const zoom = Math.min(targetWidth / lprWidthWithBorder, targetHeight / lprHeightWithBorder);
    // Center Coordinates
    const centerX = (xMax + xMin) / 2;
    const centerY = (yMax + yMin) / 2;
    // Top and Left coordinates - center the crop area
    return {
      zoom,
      left: centerX - targetWidth / zoom / 2,
      top: centerY - targetHeight / zoom / 2,
    };
  };

  const { zoom, top, left } = getPlacementOptions(plate_crop, targetWidth);

  const hover = () => setImageUrl(rawUrl);
  const unhover = () => setImageUrl(enhancedUrl);

  const toggleWithoutPropagation = (e: SyntheticEvent) => {
    toggleModal();
    e.stopPropagation();
  };

  const modalSrc = props.showRaw === true ? rawUrl : enhancedUrl;

  const handleContextMenu = (e: SyntheticEvent) => {
    if (!canViewLargeLP) {
      e.preventDefault();
    }
  };

  let platePreview;
  if (event.metadata?.homography) {
    platePreview = (
      <ZoomableImage
        disableZoom
        src={imageURL}
        event={event}
        isLPRPreview
        onMouseEnter={props.showRaw === 'hover' ? hover : undefined}
        onMouseLeave={props.showRaw === 'hover' ? unhover : undefined}
        onContextMenu={handleContextMenu}
        ariaLabel={`License Plate Preview for ${event.license_plate}`}
      />
    );
  } else {
    platePreview = (
      <img
        src={imageURL}
        alt={event.license_plate}
        onMouseEnter={props.showRaw === 'hover' ? hover : undefined}
        onMouseLeave={props.showRaw === 'hover' ? unhover : undefined}
        onContextMenu={handleContextMenu}
        aria-label={`License Plate Preview for ${event.license_plate}`}
      />
    );
  }

  return (
    <>
      <div
        className="hdn-image-crop--container"
        style={{
          width: targetWidth,
          height: targetHeight,
        }}
        onClick={canViewLargeLP ? toggleWithoutPropagation : undefined}
        tabIndex={0}
        role={canViewLargeLP ? 'button' : undefined}
        aria-label={`License Plate Preview for ${event.license_plate}.${canViewLargeLP ? ' Click to enlarge.' : ''}`}
      >
        <div
          className="hdn-image-crop--content"
          style={{
            zoom,
            top: -top,
            left: -left,
          }}
        >
          {platePreview}
        </div>
      </div>
      {modalVisible && canViewLargeLP && (
        <Modal
          isOpen
          onToggle={toggleModal}
          style={{
            maxWidth: 'min(1800px, 85vw)',
            maxHeight: '85vh',
          }}
          data-test="license-plate-modal"
        >
          <Centered>
            <Padded>
              <ZoomableImage
                src={modalSrc}
                event={event}
                drawLPRDetection={hasAccess}
                ariaLabel={`License Plate Preview for ${event.license_plate}`}
              />
            </Padded>
          </Centered>
        </Modal>
      )}
    </>
  );
}
